import { useState, useEffect } from "react";
import { Save, Trash2, Edit3, FileText, Mail, HardDrive, Calendar, Globe, Brain } from "lucide-react";
import { motion } from "framer-motion";

interface ToolsPageProps {
  mcpServices?: Record<string, boolean>;
  onUpdateMcpServices?: (services: Record<string, boolean>) => void;
}

interface MCPServer {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  enabled: boolean;
  instructions?: string;
  generalPrompt?: string;
}

const MCP_SERVERS: Record<string, Omit<MCPServer, 'id' | 'enabled' | 'instructions'>> = {
  notion: {
    name: "Notion",
    description: "Search and interact with Notion workspace",
    icon: FileText,
  },
  email: {
    name: "Email",
    description: "Read, search, and send emails via Gmail",
    icon: Mail,
  },
  drive: {
    name: "Google Drive",
    description: "Access and manage Google Drive files",
    icon: HardDrive,
  },
  calendar: {
    name: "Calendar",
    description: "Manage calendar events and scheduling",
    icon: Calendar,
  },
  websearch: {
    name: "Web Search",
    description: "Search the web for information",
    icon: Globe,
  },
  memory: {
    name: "Memory",
    description: "Store and retrieve conversational memory",
    icon: Brain,
  }
};

export function ToolsPage({ mcpServices = {}, onUpdateMcpServices }: ToolsPageProps) {
  const [servers, setServers] = useState<MCPServer[]>([]);
  const [editingServer, setEditingServer] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    // Load server configurations from localStorage
    const savedConfig = localStorage.getItem('mcp-server-config');
    const savedInstructions = savedConfig ? JSON.parse(savedConfig) : {};

    const initialServers = Object.entries(MCP_SERVERS).map(([key, server]) => ({
      ...server,
      id: key,
      enabled: mcpServices[key] !== false,
      instructions: savedInstructions[key] || '',
    }));

    setServers(initialServers);
  }, [mcpServices]);

  const handleToggleServer = (serverId: string) => {
    const updatedServices = {
      ...mcpServices,
      [serverId]: !mcpServices[serverId]
    };
    
    setServers(prev => prev.map(server => 
      server.id === serverId 
        ? { ...server, enabled: !server.enabled }
        : server
    ));
    
    onUpdateMcpServices?.(updatedServices);
    setHasChanges(true);
  };

  const handleUpdateInstructions = (serverId: string, instructions: string) => {
    setServers(prev => prev.map(server =>
      server.id === serverId
        ? { ...server, instructions }
        : server
    ));
    setHasChanges(true);
  };

  const handleSaveConfiguration = () => {
    const serverConfig = servers.reduce((acc, server) => ({
      ...acc,
      [server.id]: server.instructions || ''
    }), {});

    localStorage.setItem('mcp-server-config', JSON.stringify(serverConfig));
    setHasChanges(false);
    
    // Here you would also send the configuration to the backend
    console.log('Saved configuration:', { serverConfig });
  };

  const handleResetServer = (serverId: string) => {
    setServers(prev => prev.map(server =>
      server.id === serverId
        ? { ...server, instructions: '' }
        : server
    ));
    setHasChanges(true);
  };

  return (
    <div className="h-full bg-slate-900 text-white overflow-auto">
      <div className="p-6 border-b border-slate-800 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Tools</h1>
        {hasChanges && (
          <button
            onClick={handleSaveConfiguration}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            <Save className="w-4 h-4" />
            Save
          </button>
        )}
      </div>

      <div className="p-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {servers.map((server, index) => {
            const Icon = server.icon;
            const isEditing = editingServer === server.id;

            return (
              <motion.div
                key={server.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-slate-800/70 border rounded-xl p-4 transition-colors ${
                  server.enabled ? 'border-slate-700' : 'border-slate-800 opacity-60'
                }`}
              >
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-slate-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-white">{server.name}</h3>
                          <p className="text-xs text-slate-400">{server.description}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => handleToggleServer(server.id)} className="relative">
                            <div className={`w-11 h-6 rounded-full transition-colors ${server.enabled ? 'bg-blue-500' : 'bg-slate-600'}`}></div>
                            <motion.div className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-md" animate={{ left: server.enabled ? '22px' : '2px' }} transition={{ type: 'spring', stiffness: 500, damping: 30 }} />
                          </button>
                          <button
                            onClick={() => setEditingServer(isEditing ? null : server.id)}
                            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Click to add instructions */}
                  {server.enabled && (
                    <div className="mt-1">
                      {isEditing ? (
                        <div className="space-y-3">
                          <textarea
                            value={server.instructions || ''}
                            onChange={(e) => handleUpdateInstructions(server.id, e.target.value)}
                            placeholder={`Custom instructions for ${server.name}`}
                            className="w-full h-24 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <div className="flex gap-2 justify-end">
                            <button onClick={() => handleResetServer(server.id)} className="px-3 py-1 text-sm text-slate-400 hover:text-white transition-colors">
                              <Trash2 className="w-3 h-3" />
                            </button>
                            <button onClick={() => setEditingServer(null)} className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors">
                              Done
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div onClick={() => setEditingServer(server.id)} className="p-3 bg-slate-700/40 border border-slate-600/60 rounded-lg cursor-text hover:border-slate-500 transition-colors">
                          {server.instructions ? (
                            <p className="text-sm text-slate-300 line-clamp-3">{server.instructions}</p>
                          ) : (
                            <p className="text-sm text-slate-500 italic">Click to add instructions…</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
} 